import { ActionStatus } from "../Types";


/**
 * =====================================================
 * Types
 * =====================================================
 *
 * ActionStatusFlags represents a convenient boolean
 * breakdown of an ActionStatus value.
 */
type ActionStatusFlags = {
    isIdle: boolean;
    isLoading: boolean;
    isFailed: boolean;
    isSucceeded: boolean;
};


/**
 * =====================================================
 * Helper: ActionStatusHelper
 * =====================================================
 *
 * Description:
 * Utility class providing helper methods to evaluate
 * and combine ActionStatus values.
 *
 * Centralizes status interpretation logic in order to:
 * - keep components and hooks readable,
 * - avoid duplicated conditional checks,
 * - standardize status handling across the application.
 *
 * Author: Samano CASTRE
 * Date: July 2025
 */
export class ActionStatusHelper {

    static isLoading = (status: ActionStatus) => status === 'loading';

    static isSucceeded = (status: ActionStatus) => status === 'success';

    static isFailed = (status: ActionStatus) => status === 'error';

    static hasError = (status: ActionStatus) => status === 'error';

    static isIdle = (status: ActionStatus) => status === 'idle';

    /**
     * Returns a boolean flag object describing
     * the given ActionStatus.
     *
     * Useful for destructuring in UI logic.
     */
    static is = (status: ActionStatus) : ActionStatusFlags => {
        return {
            isIdle : status === 'idle',
            isLoading : status === 'loading',
            isFailed : status === 'error',
            isSucceeded : status === 'success'
        }
    }

    /**
     * Combines multiple ActionStatus values into a single one.
     *
     * Priority order:
     * - error    → if at least one status failed
     * - loading  → if at least one status is loading
     * - idle     → if at least one status is idle
     * - success  → only if all statuses succeeded
     *
     * Default fallback is 'idle'.
     */
    static combine(...statuses: ActionStatus[]): ActionStatus {
        if (statuses.some(status => status === 'error')) {
            return 'error';
        }
        if (statuses.some(status => status === 'loading')) {
            return 'loading';
        }
        if (statuses.some(status => status === 'idle')) {
            return 'idle';
        }
        if (statuses.every(status => status === 'success')) {
            return 'success';
        }
        return 'idle';
    }
}